import { Box, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import ModalComponent from 'modules/core/components/Modal'
import MonthPicker from 'modules/core/components/MonthPicker'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import {
  generateCompensationFromForm,
  generateForm,
} from 'modules/contracts/lib/additionalCompensations'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import Compensation, { CUSTOM_TIME_CONDITION } from 'modules/contracts/store/models/Compensation'
import type Employee from 'modules/employees/store/models/Employee'

import Definition from './_Definition'
import Explanation from './_Explanation'
import { Footer } from './_Footer'
import Schedule from './_Schedule'

interface Props {
  compensation: Compensation
  employee: Employee
  isEditable: boolean
  onSubmit: (compensation: Compensation) => void
  onRemoveCompensation: (compensation: Compensation) => void
  onClose: () => void
}

const AdditionalCompensationsModal = ({
  compensation,
  employee,
  isEditable,
  onSubmit,
  onRemoveCompensation,
  onClose,
}: Props) => {
  const recurrences = Compensation.recurrences()
  const form = useLazyRef(() => generateForm(compensation)).current

  const recurrenceOptions: SelectOption[] = Object.keys(recurrences).map((key) => ({
    label: recurrences[key] ?? '',
    value: key,
  }))

  const { contractVersionPolicy } = useContractFlow()
  const isAmountReadOnly =
    !isEditable || form.get('compensation_type').value === Compensation.types.undefined

  const isSubmitDisabled =
    !Number(form.get('contracts_taxonomy_id').value) ||
    (form.get('amount').value === '' && !isAmountReadOnly) ||
    (form.get('compensation_type').value === Compensation.types.per_worked_day &&
      !form.get('time_condition').value) ||
    (form.get('time_condition').value === CUSTOM_TIME_CONDITION &&
      !form.get('minimum_amount_of_hours').value)

  const isSyncWithSupplementsReadOnly = !isEditable || !contractVersionPolicy

  const showSyncWithSupplements =
    hasFeature('payroll_bundle') &&
    Compensation.syncableTypes.includes(form.get('compensation_type').value) &&
    !isSyncWithSupplementsReadOnly

  const isPerWorkedDay = form.get('compensation_type').value === Compensation.types.per_worked_day

  const renderRecurrence = () => (
    <Select
      label={i18n.t('contract_versions.forms.additional_compensation.modal.recurrence_field')}
      field={form.get('recurrence')}
      options={recurrenceOptions}
      readonly={!isEditable || isPerWorkedDay}
      none={i18n.t('common.none')}
    />
  )
  const renderFirstPaymentOn = () => (
    <Box>
      <MonthPicker
        label={i18n.t(
          'contract_versions.forms.additional_compensation.modal.first_payment_on_field'
        )}
        value={form.get('first_payment_on').value}
        readonly={!isEditable}
        onChange={(value: string) => form.get('first_payment_on').set(value)}
      />
    </Box>
  )

  const renderExplanation = () => {
    if (
      form.get('compensation_type').value === Compensation.types.undefined ||
      form.get('amount').value !== ''
    ) {
      return <Explanation employee={employee} form={form} />
    }

    return null
  }

  const onFormSubmit = () => {
    onSubmit(generateCompensationFromForm(form, compensation))
    onClose()
  }

  const toggleSyncWithSupplements = () => {
    form.setValues({
      sync_with_supplements: !form.fields.sync_with_supplements?.value,
    })
  }

  const onRemove = (compensation: Compensation) => {
    onRemoveCompensation(compensation)
    onClose()
  }

  return (
    <ModalComponent
      illustration="list-document"
      title={i18n.t('contract_versions.forms.additional_compensation.modal.title')}
      description={
        hasFeature('payroll_bundle')
          ? i18n.t('contract_versions.forms.additional_compensation.modal.description_for_payroll')
          : i18n.t('contract_versions.forms.additional_compensation.modal.description')
      }
      onClose={onClose}
      footer={
        <Footer
          compensation={compensation}
          handleRemove={() => onRemove(compensation)}
          isSubmitDisabled={isSubmitDisabled}
          onClick={() => onFormSubmit()}
        />
      }
    >
      {showSyncWithSupplements && (
        <Box flexDirection="row" gap="s8" paddingBottom="s16">
          <TogglerCheckbox
            value={form.get('sync_with_supplements').value}
            label={i18n.t(
              'contract_versions.forms.additional_compensation.modal.sync_with_payroll'
            )}
            onChange={toggleSyncWithSupplements}
          />
        </Box>
      )}
      <Box gap="s16">
        <Definition form={form} employee={employee} isEditable={isEditable} />

        <Schedule
          form={form}
          compensation={compensation}
          isEditable={isEditable}
          renderRecurrence={renderRecurrence}
          renderFirstPaymentOn={renderFirstPaymentOn}
        />

        <Box>{renderExplanation()}</Box>
      </Box>
    </ModalComponent>
  )
}

export default observer(AdditionalCompensationsModal)
